import React, { createContext, useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Outlet } from "react-router-dom";
import apiService from "../services/api";
import { useCampaign } from "../hooks/useCampaign";
import type { DatabaseQueryResult, ProgressInfo, DashboardData } from "../types/api";

// eslint-disable-next-line @typescript-eslint/no-empty-function
const debugLog: typeof console.log = process.env.NODE_ENV === "development" ? console.log.bind(console) : () => {};

const STALENESS_MS = 300_000; // 5 minutes

// =============================================================================
// Types
// =============================================================================

export interface EmailMetricsData {
  success: boolean;
  campaign_name: string;
  total_sent: number;
  total_opens: number;
  total_clicks: number;
  recipients_count: number;
  rows: Record<string, unknown>[];
}

export interface CampaignDataState {
  prospects: DatabaseQueryResult | null;
  connectionRequests: DatabaseQueryResult | null;
  outreachMessages: DatabaseQueryResult | null;
  responsesReceived: DatabaseQueryResult | null;
  adCampaigns: DatabaseQueryResult | null;
  emailMetrics: EmailMetricsData | null;
  isEmailMetricsLoaded: boolean;
  webinarAttendees: DatabaseQueryResult | null;
  dashboardData: DashboardData;
  totalProspectCount: number;
  isLoadingDatabase: boolean;
  loadingStates: Record<string, boolean>;
  loadingProgress: {
    prospects: ProgressInfo | null;
    connections: ProgressInfo | null;
    outreach: ProgressInfo | null;
    responses: ProgressInfo | null;
  };
  // Actions
  refetchAll: () => Promise<void>;
  refetchConversations: () => Promise<void>;
  refetchDashboardData: () => Promise<void>;
  loadMoreProspects: () => Promise<void>;
  isLoadingMoreProspects: boolean;
  loadMoreProgress: ProgressInfo | null;
}

const defaultDashboardData: DashboardData = {
  metrics: {},
  prospects: [],
  webinarAttendees: [],
  connectionRequests: [],
  messagesSent: [],
  messagesResponses: [],
};

// =============================================================================
// Context
// =============================================================================

export const CampaignDataContext = createContext<CampaignDataState | null>(null);

// =============================================================================
// Helper: deduplicate rows by composite key
// =============================================================================

function deduplicateData(
  dataArray: Record<string, unknown>[] | null | undefined,
  uniqueKeyFields: string[] = ["id"],
): Record<string, unknown>[] {
  if (!dataArray || !Array.isArray(dataArray) || dataArray.length === 0) {
    return dataArray || [];
  }

  const seen = new Set<string>();
  const deduplicated: Record<string, unknown>[] = [];

  for (const item of dataArray) {
    const uniqueKey = uniqueKeyFields
      .map((field) => item[field])
      .filter((val) => val != null && val !== "")
      .join("_");

    const finalKey =
      uniqueKey || String(item.user_id || "") || String(item.id || "") || JSON.stringify(item);

    if (!seen.has(finalKey)) {
      seen.add(finalKey);
      deduplicated.push(item);
    }
  }

  return deduplicated;
}

// =============================================================================
// Helper: group ad campaigns by ad group and aggregate metrics
// =============================================================================

function groupAdCampaigns(rows: Record<string, unknown>[]): Record<string, unknown>[] {
  const groupedMap = new Map<string, Record<string, unknown>>();

  rows.forEach((row) => {
    let groupKey: string;

    if (row.ad_group_id) {
      groupKey = `ad_group_${row.ad_group_id}`;
    } else if (row.platform_campaign_id) {
      groupKey = `platform_campaign_${row.platform_campaign_id}`;
    } else if (row.platform_ad_id) {
      groupKey = `platform_ad_${row.platform_ad_id}`;
    } else {
      const header = (row.header || row.target || row.targeting || "")
        .toString()
        .trim()
        .toLowerCase();
      const adType = (row.ad_type || "").toString().trim().toLowerCase();
      groupKey = `header_${header}__type_${adType}`;
    }

    if (!groupedMap.has(groupKey)) {
      groupedMap.set(groupKey, {
        ...row,
        impressions: row.impressions || 0,
        clicks: row.clicks || 0,
        opens: row.opens || 0,
        spend: row.spend || 0,
        _count: 1,
      });
    } else {
      const existing = groupedMap.get(groupKey)!;
      existing.impressions = ((existing.impressions as number) || 0) + ((row.impressions as number) || 0);
      existing.clicks = ((existing.clicks as number) || 0) + ((row.clicks as number) || 0);
      existing.opens = ((existing.opens as number) || 0) + ((row.opens as number) || 0);
      existing.spend = ((existing.spend as number) || 0) + ((row.spend as number) || 0);
      existing._count = ((existing._count as number) || 0) + 1;

      if ((existing.clicks as number) > 0 && (existing.spend as number) > 0) {
        existing.cost_per_click = (existing.spend as number) / (existing.clicks as number);
      } else if (row.cost_per_click) {
        existing.cost_per_click = row.cost_per_click;
      }

      if (
        row.updated_at &&
        (!existing.updated_at || new Date(row.updated_at as string) > new Date(existing.updated_at as string))
      ) {
        existing.updated_at = row.updated_at;
      }
      if (
        row.created_at &&
        (!existing.created_at || new Date(row.created_at as string) > new Date(existing.created_at as string))
      ) {
        existing.created_at = row.created_at;
      }
    }
  });

  return Array.from(groupedMap.values()).map((row) => {
    const { _count, ...cleanRow } = row;
    return cleanRow;
  });
}

// =============================================================================
// Provider
// =============================================================================

export const CampaignDataProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { campaign: resolvedCampaign, loading: campaignLoading } = useCampaign();

  // Data state
  const [prospects, setProspects] = useState<DatabaseQueryResult | null>(null);
  const [connectionRequests, setConnectionRequests] = useState<DatabaseQueryResult | null>(null);
  const [outreachMessages, setOutreachMessages] = useState<DatabaseQueryResult | null>(null);
  const [responsesReceived, setResponsesReceived] = useState<DatabaseQueryResult | null>(null);
  const [adCampaigns, setAdCampaigns] = useState<DatabaseQueryResult | null>(null);
  const [emailMetrics, setEmailMetrics] = useState<EmailMetricsData | null>(null);
  const [isEmailMetricsLoaded, setIsEmailMetricsLoaded] = useState<boolean>(false);
  const [webinarAttendees, setWebinarAttendees] = useState<DatabaseQueryResult | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardData>(defaultDashboardData);
  const [totalProspectCount, setTotalProspectCount] = useState<number>(0);
  const [isLoadingDatabase, setIsLoadingDatabase] = useState<boolean>(true);
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({
    prospects: true,
    accounts: true,
    messages: true,
    emails: true,
    impressions: true,
    clicks: true,
  });
  const [loadingProgress, setLoadingProgress] = useState({
    prospects: null as ProgressInfo | null,
    connections: null as ProgressInfo | null,
    outreach: null as ProgressInfo | null,
    responses: null as ProgressInfo | null,
  });
  const [isLoadingMoreProspects, setIsLoadingMoreProspects] = useState<boolean>(false);
  const [loadMoreProgress, setLoadMoreProgress] = useState<ProgressInfo | null>(null);

  // Staleness tracking
  const lastFetchedAtRef = useRef<Record<string, number>>({});
  // Track current campaign to detect changes
  const prevCampaignIdRef = useRef<string | null>(null);

  const shouldFetch = useCallback((key: string): boolean => {
    const lastFetched = lastFetchedAtRef.current[key];
    if (!lastFetched) return true;
    return Date.now() - lastFetched >= STALENESS_MS;
  }, []);

  const markFetched = useCallback((key: string) => {
    lastFetchedAtRef.current[key] = Date.now();
  }, []);

  // ── Individual fetch functions ──────────────────────────────────────────

  const fetchDashboardData = useCallback(async (): Promise<void> => {
    try {
      const data = await apiService.getDashboardData();
      setDashboardData(data);
      markFetched("dashboard");
    } catch (err) {
      console.error("Failed to fetch dashboard data:", err);
    }
  }, [markFetched]);

  const fetchProspects = useCallback(async (campaign: { campaign_id: string }): Promise<void> => {
    try {
      const results = await apiService.getCombinedProspectList(
        campaign.campaign_id,
        2000,
        (progress) => {
          setLoadingProgress((prev) => ({ ...prev, prospects: progress }));
        },
      );
      setLoadingProgress((prev) => ({ ...prev, prospects: null }));

      const totalCount = Number(results?.totalRows ?? results?.rowCount ?? 0);
      setTotalProspectCount(totalCount);

      if (results && results.success && results.rows) {
        const deduplicatedResults = {
          ...results,
          rows: deduplicateData(results.rows, ["id", "user_id"]),
        };
        setProspects(deduplicatedResults);
      } else {
        setProspects(results);
      }
      markFetched("prospects");
    } catch (err) {
      console.error("Failed to load prospect list:", err);
    } finally {
      setLoadingStates((prev) => ({ ...prev, prospects: false }));
    }
  }, [markFetched]);

  const fetchConnectionRequests = useCallback(async (campaign: { campaign_name: string }): Promise<void> => {
    try {
      const results = await apiService.getConnectionRequests(campaign.campaign_name);
      if (results.success) {
        setConnectionRequests({
          ...results,
          rows: deduplicateData(results.rows ?? [], ["id", "recipient_name", "sent_date"]),
        });
      } else {
        setConnectionRequests(null);
      }
      markFetched("connectionRequests");
    } catch (err) {
      console.error("Failed to load connection requests:", err);
      setConnectionRequests(null);
    } finally {
      setLoadingStates((prev) => ({ ...prev, accounts: false }));
    }
  }, [markFetched]);

  const fetchOutreachMessages = useCallback(async (campaign: { campaign_name: string }): Promise<void> => {
    try {
      const results = await apiService.getMessagesSent(campaign.campaign_name);
      if (results.success) {
        setOutreachMessages({
          ...results,
          rows: deduplicateData(results.rows ?? [], ["id", "recipient_name", "platform", "sent_date"]),
        });
      } else {
        setOutreachMessages(null);
      }
      markFetched("outreachMessages");
    } catch (err) {
      console.error("Failed to load outreach messages:", err);
      setOutreachMessages(null);
    } finally {
      setLoadingStates((prev) => ({ ...prev, messages: false }));
    }
  }, [markFetched]);

  const fetchResponsesReceived = useCallback(async (campaign: { campaign_name: string }): Promise<void> => {
    try {
      const unifiedResults = await apiService.getUnifiedConversations(campaign.campaign_name, undefined, 1000);
      const results = {
        success: unifiedResults.success,
        rows: unifiedResults.rows as unknown as Record<string, unknown>[],
        rowCount: unifiedResults.rowCount,
        error: unifiedResults.error,
      };

      if (results.success && results.rows) {
        setResponsesReceived({
          ...results,
          rows: deduplicateData(results.rows, ["source", "id"]),
        });
      } else {
        setResponsesReceived(null);
      }
      markFetched("responsesReceived");
    } catch (err) {
      console.error("Failed to load responses:", err);
      setResponsesReceived(null);
    }
  }, [markFetched]);

  const fetchAdCampaigns = useCallback(async (campaign: { campaign_id: string }): Promise<void> => {
    try {
      const firstWord = campaign.campaign_id === "__all__"
        ? "__all__"
        : campaign.campaign_id.split("_")[0];

      const results = await apiService.getAdvertisingPerformance(firstWord);

      if (results.success) {
        setAdCampaigns({
          ...results,
          rows: groupAdCampaigns(results.rows ?? []),
        });
      } else {
        setAdCampaigns(null);
      }
      markFetched("adCampaigns");
    } catch (err) {
      console.error("Failed to load ad campaigns:", err);
      setAdCampaigns(null);
    } finally {
      setLoadingStates((prev) => ({ ...prev, impressions: false, clicks: false }));
    }
  }, [markFetched]);

  const fetchEmailMetrics = useCallback(async (campaign: { campaign_name: string }): Promise<void> => {
    setIsEmailMetricsLoaded(false);
    try {
      const results = await apiService.getEmailMetrics(campaign.campaign_name);

      if (results.success) {
        const emailRows = results.rows ?? [];
        const totalSent = emailRows.reduce(
          (sum: number, row: Record<string, unknown>) => sum + (parseInt(String(row.total_sent)) || 0),
          0,
        );
        const totalOpens = emailRows.reduce(
          (sum: number, row: Record<string, unknown>) => sum + (parseInt(String(row.total_opens)) || 0),
          0,
        );
        const totalClicks = emailRows.reduce(
          (sum: number, row: Record<string, unknown>) => sum + (parseInt(String(row.total_clicks)) || 0),
          0,
        );
        const recipientsCount = emailRows.length > 0
          ? (parseInt(String((emailRows[0] as Record<string, unknown>).recipients_count)) || 0)
          : 0;

        setEmailMetrics({
          success: true,
          campaign_name: campaign.campaign_name,
          total_sent: totalSent,
          total_opens: totalOpens,
          total_clicks: totalClicks,
          recipients_count: recipientsCount,
          rows: emailRows,
        });
      } else {
        setEmailMetrics(null);
      }
      markFetched("emailMetrics");
    } catch (err) {
      console.error("Failed to load email metrics:", err);
      setEmailMetrics(null);
    } finally {
      setIsEmailMetricsLoaded(true);
      setLoadingStates((prev) => ({ ...prev, emails: false }));
    }
  }, [markFetched]);

  const fetchWebinarAttendees = useCallback(async (campaign: { campaign_id: string; webinar_id?: string | null }): Promise<void> => {
    try {
      const webinarCampaignId = campaign.webinar_id || (/_Webinar_/i.test(campaign.campaign_id) ? campaign.campaign_id : null);
      if (!webinarCampaignId) {
        setWebinarAttendees(null);
        markFetched("webinarAttendees");
        return;
      }

      const results = await apiService.getWebinarAttendees(webinarCampaignId);

      if (results.success) {
        setWebinarAttendees({
          ...results,
          rows: deduplicateData(results.rows ?? [], ["id", "user_id", "emails"]),
        });
      } else {
        setWebinarAttendees(null);
      }
      markFetched("webinarAttendees");
    } catch (err) {
      console.error("Failed to load webinar attendees:", err);
      setWebinarAttendees(null);
    }
  }, [markFetched]);

  // ── Load all data ───────────────────────────────────────────────────────

  const loadAllData = useCallback(async (campaign: {
    campaign_id: string;
    campaign_name: string;
    webinar_id?: string | null;
    features?: { showWebinarAttendees?: boolean };
  }, force = false): Promise<void> => {
    debugLog(`[CampaignDataContext] Loading data for campaign: ${campaign.campaign_id}`);
    setIsLoadingDatabase(true);

    const promises: Promise<void>[] = [];

    if (force || shouldFetch("dashboard")) promises.push(fetchDashboardData());
    if (force || shouldFetch("prospects")) promises.push(fetchProspects(campaign));
    if (force || shouldFetch("connectionRequests")) promises.push(fetchConnectionRequests(campaign));
    if (force || shouldFetch("outreachMessages")) promises.push(fetchOutreachMessages(campaign));
    if (force || shouldFetch("responsesReceived")) promises.push(fetchResponsesReceived(campaign));
    if (force || shouldFetch("adCampaigns")) promises.push(fetchAdCampaigns(campaign));
    if (force || shouldFetch("emailMetrics")) promises.push(fetchEmailMetrics(campaign));

    if (campaign.features?.showWebinarAttendees && (force || shouldFetch("webinarAttendees"))) {
      promises.push(fetchWebinarAttendees(campaign));
    }

    if (promises.length === 0) {
      debugLog("[CampaignDataContext] All data is fresh, skipping fetch");
      setIsLoadingDatabase(false);
      return;
    }

    await Promise.all(promises);
    debugLog("[CampaignDataContext] All data loaded successfully");
    setIsLoadingDatabase(false);
  }, [shouldFetch, fetchDashboardData, fetchProspects, fetchConnectionRequests, fetchOutreachMessages, fetchResponsesReceived, fetchAdCampaigns, fetchEmailMetrics, fetchWebinarAttendees]);

  // ── Campaign change → reset + load ─────────────────────────────────────

  useEffect(() => {
    if (!resolvedCampaign || campaignLoading) return;

    const campaignId = resolvedCampaign.campaign_id;
    const isNewCampaign = prevCampaignIdRef.current !== campaignId;

    if (isNewCampaign) {
      debugLog(`[CampaignDataContext] Campaign changed: ${prevCampaignIdRef.current} → ${campaignId}`);
      prevCampaignIdRef.current = campaignId;

      // Reset staleness tracking so everything refetches
      lastFetchedAtRef.current = {};

      // Reset all state
      setProspects(null);
      setConnectionRequests(null);
      setOutreachMessages(null);
      setResponsesReceived(null);
      setAdCampaigns(null);
      setEmailMetrics(null);
      setIsEmailMetricsLoaded(false);
      setWebinarAttendees(null);
      setDashboardData(defaultDashboardData);
      setTotalProspectCount(0);
      setLoadingStates({
        prospects: true,
        accounts: true,
        messages: true,
        emails: true,
        impressions: true,
        clicks: true,
      });
    }

    loadAllData(resolvedCampaign, isNewCampaign);
  }, [resolvedCampaign?.campaign_id, campaignLoading, loadAllData]);

  // ── Polling: only dashboard data, visibility-gated ─────────────────────

  useEffect(() => {
    if (!resolvedCampaign || campaignLoading) return;

    let intervalId: ReturnType<typeof setInterval> | null = null;

    const startPolling = () => {
      if (intervalId) return;
      intervalId = setInterval(() => {
        debugLog("[CampaignDataContext] Polling dashboard data...");
        fetchDashboardData();
      }, 30000);
    };

    const stopPolling = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        fetchDashboardData();
        startPolling();
      }
    };

    startPolling();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [resolvedCampaign?.campaign_id, campaignLoading, fetchDashboardData]);

  // ── Load More Prospects ────────────────────────────────────────────────

  const handleLoadMoreProspects = useCallback(async (): Promise<void> => {
    if (!resolvedCampaign?.campaign_id) return;

    try {
      setIsLoadingMoreProspects(true);
      setLoadMoreProgress(null);

      const currentCount = prospects?.rows?.length ?? 0;
      const loadCount = 2000;

      const results = await apiService.loadMoreProspects(
        resolvedCampaign.campaign_id,
        currentCount,
        loadCount,
        (progress) => {
          setLoadMoreProgress(progress);
        },
      );

      setLoadMoreProgress(null);

      if (results.success && results.rows && results.rows.length > 0) {
        const existingRows = prospects?.rows ?? [];
        const updatedProspects = [...existingRows, ...results.rows];

        setProspects({
          success: true,
          ...(prospects ?? {}),
          rows: updatedProspects,
          rowCount: updatedProspects.length,
          hasMore: results.hasMore,
        });

        debugLog(`[CampaignDataContext] Loaded ${results.rows.length} more prospects. Total: ${updatedProspects.length}`);
      }
    } catch (err) {
      console.error("Failed to load more prospects:", err);
    } finally {
      setIsLoadingMoreProspects(false);
    }
  }, [resolvedCampaign?.campaign_id, prospects]);

  // ── Refetch all (force) ────────────────────────────────────────────────

  const refetchAll = useCallback(async (): Promise<void> => {
    if (!resolvedCampaign || campaignLoading) return;
    lastFetchedAtRef.current = {};
    await loadAllData(resolvedCampaign, true);
  }, [resolvedCampaign, campaignLoading, loadAllData]);

  const refetchConversations = useCallback(async (): Promise<void> => {
    if (!resolvedCampaign || campaignLoading) return;
    await fetchResponsesReceived(resolvedCampaign);
  }, [resolvedCampaign, campaignLoading, fetchResponsesReceived]);

  // ── Context value ──────────────────────────────────────────────────────

  const contextValue = useMemo<CampaignDataState>(
    () => ({
      prospects,
      connectionRequests,
      outreachMessages,
      responsesReceived,
      adCampaigns,
      emailMetrics,
      isEmailMetricsLoaded,
      webinarAttendees,
      dashboardData,
      totalProspectCount,
      isLoadingDatabase,
      loadingStates,
      loadingProgress,
      refetchAll,
      refetchConversations,
      refetchDashboardData: fetchDashboardData,
      loadMoreProspects: handleLoadMoreProspects,
      isLoadingMoreProspects,
      loadMoreProgress,
    }),
    [
      prospects,
      connectionRequests,
      outreachMessages,
      responsesReceived,
      adCampaigns,
      emailMetrics,
      isEmailMetricsLoaded,
      webinarAttendees,
      dashboardData,
      totalProspectCount,
      isLoadingDatabase,
      loadingStates,
      loadingProgress,
      refetchAll,
      refetchConversations,
      fetchDashboardData,
      handleLoadMoreProspects,
      isLoadingMoreProspects,
      loadMoreProgress,
    ],
  );

  return (
    <CampaignDataContext.Provider value={contextValue}>
      {children ?? <Outlet />}
    </CampaignDataContext.Provider>
  );
};

export default CampaignDataProvider;
